/**
 * Utility functions for detecting and managing variables in text fields
 */

/**
 * Extracts variable names from text that contain common Go template patterns
 *
 * Cases it handles:
 * - Simple: .Field, $variable → Field, variable
 * - Dotted: .User.Name, .Config.Database.Host → User.Name, Config.Database.Host
 * - Control: if .Condition, range .Items, with .User → Condition, Items, User
 * - Expressions: if and .IsActive .HasPermission → IsActive, HasPermission
 * - Functions: len .Items, .Field | printf → Items, Field (ignores len, printf)
 * - Comments: ignores anything in comment blocks
 * - Keywords: filters out if, else, end, range, with, template, and, or, not, eq, len
 *
 * @param text The text to extract variables from
 * @returns Array of variable names found in the text
 */
export function extractVariables(text: string | null | undefined): string[] {
  if (!text) return [];

  const variables: string[] = [];

  // Find all template blocks {{...}} but exclude comments {{/* ... */}}
  const templateBlocksMatch = text.match(/\{\{(?!\s*\/\*)[^}]*\}\}/g);
  const templateBlocks: string[] = templateBlocksMatch || [];

  // Pattern to match Go template variables: .Field, $var, .User.Name
  const variablePattern = /([$.][ a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*)/g;

  templateBlocks.forEach((block) => {
    // Skip comment blocks
    if (block && block.includes('/*')) {
      return;
    }

    let match;
    variablePattern.lastIndex = 0;

    while ((match = variablePattern.exec(block)) !== null) {
      let variableName = match[1].trim();

      // Normalize: remove leading $ or .
      if (variableName.startsWith('.')) {
        variableName = variableName.substring(1);
      } else if (variableName.startsWith('$')) {
        variableName = variableName.substring(1);
      }

      // Skip common Go template keywords
      if (isGoTemplateKeyword(variableName)) {
        continue;
      }

      // Add if valid and not already included
      if (variableName && !variables.includes(variableName)) {
        variables.push(variableName);
      }
    }
  });

  return variables;
}

/**
 * Checks if a variable name is actually a Go template keyword/function
 */
function isGoTemplateKeyword(variableName: string): boolean {
  const keywords = ['if', 'else', 'end', 'range', 'with', 'template', 'and', 'or', 'not', 'eq', 'len'];
  return keywords.includes(variableName.toLowerCase());
}

/**
 * Validates a variable name according to Go template naming conventions
 * Supports dotted field names like "user.name" and simple names like "username"
 * @param variableName The variable name to validate
 * @returns True if the variable name is valid
 */
export function isValidVariableName(variableName: string): boolean {
  // Allow dotted field names (user.name) and simple names (username)
  return /^[a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*$/.test(variableName);
}

/**
 * Extracts variables from multiple text sources
 * @param textSources Array of text sources to extract variables from
 * @returns Array of unique variable names found across all sources
 */
export function extractVariablesFromMultipleSources(textSources: (string | null | undefined)[]): string[] {
  const allVariables: string[] = [];

  textSources.forEach((text) => {
    const variables = extractVariables(text);
    variables.forEach((variable) => {
      if (!allVariables.includes(variable)) {
        allVariables.push(variable);
      }
    });
  });

  return allVariables;
}
